import Swup from 'swup';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import intersect from '@alpinejs/intersect';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-cards';
import 'swiper/css/free-mode';

// Global Alpine components
import './components/appShell.js';
import './components/projectsGrid.js';
import './components/news.js';

// Block / feature components
import './components/accordion.js';
import './components/rule.js';
import './components/spacer.js';
import './components/cursorFollower.js';
import { initCarousel } from './components/swiper.js';
import { initHomeReelCarousel } from './components/homeReelCarousel.js';

window.isMobile = window.matchMedia('only screen and (max-width: 720px)').matches;

// Register Alpine plugins before starting
Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.plugin(intersect);

// Make Alpine available globally so components can reach in
window.Alpine = Alpine;
window.initCarousel = initCarousel;

// Sync playback of videos inside the start overlay's right pane to the
// overlay's open/closed state. The carousel's own IntersectionObserver
// pauses the slide-advance *timer* while off-screen, but doesn't pause
// videos — so without this, the About page's video would keep playing
// (and decoding) in the background whenever the panel is closed.
function syncStartOverlayVideos() {
  const startElement = document.getElementById('start-a-project');
  if (!startElement) return;
  const isOpen = startElement.classList.contains('show');
  startElement.querySelectorAll('.start-right video').forEach((video) => {
    if (isOpen) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    } else {
      video.pause();
    }
  });
}

// Start a Project overlay state management - watch for hash changes
const initStartProjectOverlay = () => {
  const startElement = document.getElementById('start-a-project');

  // Keep `inert` in sync with the `show` class so the overlay is
  // completely non-focusable and non-clickable while off-screen, and
  // move focus into the form on open. We watch the class attribute
  // rather than toggling inert in every call site — this stays correct
  // no matter which code path (hash poll here, Escape/close handlers
  // in start.php) toggles `show`.
  if (startElement) {
    const syncOverlayState = () => {
      const isOpen = startElement.classList.contains('show');
      const wasOpen = !startElement.hasAttribute('inert');

      syncStartOverlayVideos();

      if (isOpen) {
        startElement.removeAttribute('inert');
        if (!wasOpen) {
          // Just transitioned from hidden to shown — move focus into
          // the form so keyboard/AT users can start filling it in
          // straight away, instead of being stranded on the trigger.
          const focusTarget =
            startElement.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])') ||
            startElement.querySelector('button:not([disabled])');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
      } else {
        startElement.setAttribute('inert', '');
      }
    };
    syncOverlayState();
    new MutationObserver(syncOverlayState).observe(startElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Close the overlay when focus leaves it — but only if the new
    // focus target isn't a "Start a project" trigger link. This lets
    // keyboard users tab out of the last field to dismiss, and lets
    // shift-tab back to the trigger keep the panel open.
    startElement.addEventListener('focusout', (e) => {
      if (!startElement.classList.contains('show')) return;
      const next = e.relatedTarget;
      // Unknown target (window blur, focus dropped to nothing) — keep
      // the overlay open so we don't wipe a half-filled form.
      if (!next) return;
      if (startElement.contains(next)) return;
      if (next.matches && next.matches('a[href="#start-a-project"]')) return;
      startElement.classList.remove('show');
      history.replaceState(null, '', window.location.pathname);
    });
  }

  let currentState = false;

  const checkHash = () => {
    const shouldBeOpen = window.location.hash === '#start-a-project';

    if (currentState !== shouldBeOpen) {
      l('🔄 Hash state changed - updating overlay to: ' + shouldBeOpen);
      currentState = shouldBeOpen;

      if (startElement) {
        if (shouldBeOpen) {
          startElement.classList.add('show');
          l('✅ Added show class to start overlay');
        } else {
          startElement.classList.remove('show');
          l('❌ Removed show class from start overlay');
        }
      }
    }
  };

  // Check immediately
  checkHash();

  // Listen for hash changes
  window.addEventListener('hashchange', checkHash);

  // Polling as fallback (every 500ms)
  setInterval(checkHash, 500);
};

// Initialize on DOM ready (separate listener like naturalsleep)
document.addEventListener('DOMContentLoaded', initStartProjectOverlay);

// Swup page-transition setup
const swup = new Swup({
  containers: ['#swup'],
  animationSelector: '[class*="transition-"]',
  animateHistoryBrowsing: true,
});
window.swup = swup;

// Sync body[class] to the incoming page on every Swup navigation.
// Swup only replaces #swup, so body attributes set by the template
// (template class, --mask-height, etc.) must be copied across manually.
swup.hooks.on('content:replace', (visit) => {
  const newBody = visit?.to?.document?.body;
  if (newBody) {
    document.body.className = newBody.className;
  }
  init();
});

document.addEventListener('DOMContentLoaded', () => {
  l('ready');
  Alpine.start();
  init();
});

// Functions

// Better console.log — only on local .test urls
function l(m) {
  if (window.location.hostname.split('.').at(-1) === 'test') {
    console.log(m);
  }
}

// Measure the rendered width of .page-label__title and expose it as
// --pg-label-w on :root. Consumed by SCSS $pgLabel so the .is-open
// translate matches the actual label width per page (and stays correct
// as fluid typography scales with viewport width).
let pageLabelObserver = null;
function updatePageLabelWidth() {
  const el = document.querySelector('.page-label__title');
  if (!el) return;
  const w = Math.ceil(el.getBoundingClientRect().width);
  if (w > 0) {
    document.documentElement.style.setProperty('--pg-label-w', `${w}px`);
  }
}
function watchPageLabelWidth() {
  if (pageLabelObserver) pageLabelObserver.disconnect();
  const el = document.querySelector('.page-label__title');
  if (!el) return;
  pageLabelObserver = new ResizeObserver(updatePageLabelWidth);
  pageLabelObserver.observe(el);
  updatePageLabelWidth();
}

// Same pattern for .projects__start — exposes --start-label-w for SCSS
// $startLabel.
let startLabelObserver = null;
function updateStartLabelWidth() {
  const el = document.querySelector('.projects__start');
  if (!el) return;
  const w = Math.ceil(el.getBoundingClientRect().width);
  if (w > 0) {
    document.documentElement.style.setProperty('--start-label-w', `${w}px`);
  }
}
function watchStartLabelWidth() {
  if (startLabelObserver) startLabelObserver.disconnect();
  const el = document.querySelector('.projects__start');
  if (!el) return;
  startLabelObserver = new ResizeObserver(updateStartLabelWidth);
  startLabelObserver.observe(el);
  updateStartLabelWidth();
}

// Update :root CSS variables from page data attributes
function updateRootColors() {
  const main = document.getElementById('swup');
  if (!main) return;

  const bgColourScheme = main.dataset.bgColours;
  const root = document.documentElement;

  // Define color schemes based on bgColours value
  const colorSchemes = {
    white: {
      bg: '#FFFFFF',
      text: '#000000'
    },
    black: {
      bg: '#000000',
      text: '#FFFFFF'
    },
    brightOrange: {
      bg: '#FD4A2A',
      text: '#000000'
    },
    blue: {
      bg: '#ACC7C9',
      text: '#000000'
    }
  };

  // Get the color scheme or use default (white)
  const scheme = colorSchemes[bgColourScheme] || colorSchemes.white;

  // Apply the colors to CSS variables
  root.style.setProperty('--bg-color', scheme.bg);
  root.style.setProperty('--text-color', scheme.text);
}

function init() {
  l('👍');
  updateRootColors();
  watchPageLabelWidth();
  watchStartLabelWidth();
  if (typeof window.initCarousel === 'function') {
    const carousels = document.querySelectorAll('.carousel-container:not([data-carousel-initialized])');
    carousels.forEach((container) => {
      container.setAttribute('data-carousel-initialized', 'true');
      window.initCarousel(container);
    });
  }
  // Initialize home reel carousel
  initHomeReelCarousel();
  // initCarousel calls video.play() on the active slide's videos during
  // startAutoplay(true), so re-run the overlay-video sync afterwards to
  // pause any that shouldn't be playing while the panel is closed.
  syncStartOverlayVideos();
}
